import { Button, Typography } from "@mui/material";
import MyGuideIcon from "./MyGuideIcon";
import "./styles.css";
import { sxMyGuidesCard } from "./sxProps";
import { MyGuidesCardProps } from "../../../interfaces/props/GuiasEAutorizacoes/MyGuidesCardProps";
import {
  getCartaNegativa,
  getCTE,
  getGuiaSADT,
  getDocumentoGuia,
  anexarDocumentoGuia,
  concluirSolicitacao,
} from "../../../services/MinhasGuiasService";
import { base64toBlob } from "../../../utils/Base64ToBlob";
import { useEffect, useId, useState } from "react";
import { useAccessibility } from "../../../Store/contexts/AccessibilityContext";
import LoadingApp from "../../../components/LoadingApp";
import ModalFeedBack from "../../../components/ModalFeedBack";
import AnexoButton, { Anexo } from "./AnexoButton";
import DefaultButton from "../../../components/Accessibility/DefaultButton";
import ModalAction from "../../../components/ModalAction";
import { sxButtonText } from "./sxProps";

function MyGuidesCard(props: MyGuidesCardProps) {
  const [status, setStatus] = useState<string>("");
  const [anexos, setAnexos] = useState<Array<Anexo>>([]);
  const [anexosTemp, setAnexosTemp] = useState<Array<Anexo>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [modalFeedback, setModalFeedback] = useState<null | {
    icon?: "error" | "success" | "warning";
    title?: string;
    textButton?: string;
    subTitle?: string;
    closeAction: () => void;
    showButtonModal: boolean;
    buttonAction?: () => void;
  }>(null);
  const [modalAction, setModalAction] = useState<null | {
    handleShowModal?: () => void;
    textOutlinedButton: string;
    textContainedButton: string;
    title: string;
    subTitle?: any;
    handleOutlinedButton?: () => void;
    handleContainedButton?: () => void;
    icon: "error" | "success" | "warning";
  }>(null);

  const listContainerId = useId();

  const accessibility = useAccessibility();

  const salvarAnexos = async (anexos: Array<Anexo>) => {
    try {
      setLoading(true);
      for (const anexo of anexos) {
        await anexarDocumentoGuia(props.guide.idSolic, anexo.tipoArquivo, anexo.arquivo);
      }

      await concluirSolicitacao(props.guide.idSolic)

      openModalFeedback(
        "success",
        "Sucesso",
        "Anexos salvos com sucesso!",
        "Fechar",
        closeModalFeedback,
        closeModalFeedback
      );
      
      if (props.onDataUpdate) {
        props.onDataUpdate();
      }
    } catch (e) {
      console.error("Erro ao salvar anexos:", e);
      openModalFeedback(
        "error",
        "Erro",
        "Erro ao salvar anexo:",
        "Fechar",
        closeModalFeedback,
        closeModalFeedback
      );
    } finally {
      setLoading(false);
    }
  };

  const removerAnexoTemporario = (anexoIndex: number) => {
    const novosAnexos = [...anexosTemp];
    novosAnexos.splice(anexoIndex, 1);
    setAnexosTemp(novosAnexos);
  };

  const verAnexo = async (anexoIndex: number) => {
    try {
      setLoading(true);
      const toView = anexos[anexoIndex];
      if (toView.numAnexo) {
        const anexo = await getDocumentoGuia(
          props.guide.idSolic,
          toView.numAnexo,
          toView.tipoArquivo
        );
        if (anexo?.fileb64) {
          const link = document.createElement("a");
          link.href = anexo.fileb64;
          link.download = anexo.nome || toView.nomeArquivo;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    } catch (e) {
      openModalFeedback(
        "error",
        "Erro",
        "Erro ao baixar o anexo.",
        "Fechar",
        closeModalFeedback,
        closeModalFeedback
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (props.guide.status === "EM ANALISE") {
      setStatus("em-analise");
    } else if (props.guide.status === "AUTORIZADA") {
      setStatus("autorizada");
    } else {
      setStatus("entre-em-contato");
    }
  }, []);

  const openModalFeedback = (
    icon: "error" | "success" | "warning",
    title: string,
    subtitle: string,
    textButton: string,
    closeAction: () => void,
    buttonAction: () => void
  ) => {
    setModalFeedback({
      icon: icon,
      title: title,
      subTitle: subtitle,
      textButton: textButton,
      showButtonModal: true,
      closeAction: closeAction,
      buttonAction: buttonAction,
    });
  };

  const closeModalFeedback = () => {
    setModalFeedback(null);
  };

  const handleClick = async () => {
    setLoading(true);
    try {
      let pdf: string;
      if (props.guide.tipo_relatorio === "TISS") {
        pdf = await getGuiaSADT(props.guide.idSolic);
      } else {
        pdf = await getCTE(props.guide.idSolic);
      }
      const blob = base64toBlob(pdf.split(",")[1]);
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl);
    } catch (err) {
      openModalFeedback(
        "error",
        "Erro",
        "Erro ao gerar Carta de Negativa",
        "Fechar",
        closeModalFeedback,
        closeModalFeedback
      );
    } finally {
      setLoading(false);
    }
  };

  function handleGetCartaNegativa() {
    setLoading(true);
    getCartaNegativa(props.guide.idSolic)
      .then((pdf) => {
        const blob = base64toBlob(pdf.split(",")[1]);
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl);
      })
      .catch((err) => {
        openModalFeedback(
          "error",
          "Erro",
          "Erro ao gerar Carta de Negativa",
          "Fechar",
          closeModalFeedback,
          closeModalFeedback
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const adicionarAnexoTemp = (anexos: Array<Anexo>) => {
    setAnexosTemp(anexos);
  };

  const handleCloseModalAction = () => {
    setModalAction(null);
  };

  const confirmarAnexo = () => {
    setModalAction({
      title: "Confirmar anexos",
      subTitle: (
        <div>
          Você irá anexar o(s) documento(s):
          {anexosTemp.map((anexo, index) => (
            <div key={index}>{anexo.nomeArquivo}</div>
          ))}
          <div style={{ marginTop: "20px" }}>
            Ao confirmar, não será possível removê-los.
          </div>
        </div>
      ),
      textOutlinedButton: "Cancelar",
      textContainedButton: "Confirmar anexos",
      handleContainedButton: () => confirmarEnvioAnexos(),
      handleOutlinedButton: handleCloseModalAction,
      handleShowModal: handleCloseModalAction,
      icon: "success",
    });
  };

  const confirmarEnvioAnexos = async () => {
    await salvarAnexos(anexosTemp);
    setAnexosTemp([]);
    handleCloseModalAction();
  };

  const openModalAbandonarAnexo = () => {
    setModalAction({
      title: "Atenção",
      subTitle: `Deseja continuar sem confirmar os anexos à sua guia? Os arquivos ficarão guardados por um tempo caso você deseje retornar.`,
      textOutlinedButton: "Cancelar",
      textContainedButton: "Continuar sem confirmar",
      handleContainedButton: () => cancelarAnexo(),
      handleOutlinedButton: handleCloseModalAction,
      handleShowModal: handleCloseModalAction,
      icon: "warning",
    });
  };

  const cancelarAnexo = () => {
    setAnexosTemp([]);
    handleCloseModalAction();
  };

  return (
    <div
      className="my-guides-card-holder"
      style={{
        ...accessibility.contrastStyle,
        ...accessibility.contrastBorderAccessibility,
      }}
    >
      <LoadingApp showLoading={loading} />
      <ModalFeedBack
        showModal={!!modalFeedback}
        handleShowModal={modalFeedback?.closeAction}
        title={modalFeedback?.title as string}
        subTitle={modalFeedback?.subTitle as string}
        icon={modalFeedback?.icon as "success" | "error" | "warning"}
        showButtonModal={modalFeedback?.showButtonModal}
        textButton={modalFeedback?.textButton as string}
        handleButtonModal={modalFeedback?.buttonAction}
      />
      <div className="my-guides-card-icon-holder">
        <MyGuideIcon />
      </div>
      <div className="my-guides-card-info-holder">
        <span
          style={accessibility.textStyleAccessibility}
          className="my-guides-card-info"
        >
          <b>Solicitante:</b> {props.guide.solicitante}
        </span>
        <span
          style={accessibility.textStyleAccessibility}
          className="my-guides-card-info"
        >
          <b>Guia:</b> {props.guide.idSolic}
        </span>
        <span
          style={accessibility.textStyleAccessibility}
          className="my-guides-card-info"
        >
          <b>Emissão:</b> {props.guide.emissao}
        </span>
        <span
          style={accessibility.textStyleAccessibility}
          className="my-guides-card-info"
        >
          <b>Validade:</b> {props.guide.validade}
        </span>
        <span
          style={accessibility.textStyleAccessibility}
          className={`my-guides-card-info ${status}`}
        >
          <b>Status:</b> {props.guide.status}
        </span>
        <div className="guia-anexos">
        <span
          style={accessibility.textStyleAccessibility}
          className="my-guides-card-info"
        >
          <b>Anexo(s):</b>
        </span>
        <div id={listContainerId} className="guias-anexos-container">
          {props.guide.anexosSolic && props.guide.anexosSolic.length > 0 && (
            <div className="guia-anexos-list-container">
            {props.guide.anexosSolic.map((anexo, index) => (
              <Typography
              key={index}
              style={{
                ...accessibility.textStyleAccessibility,
                color: accessibility.contrastMode ? "#fff" : "#383838",
                fontWeight: 300,
                padding: 0,
                marginRight: 0,
              }}
              sx={sxButtonText}
            >
              {anexo.nome}
            </Typography>
            ))}
          </div>
          )}
        </div>
        </div>
        <div className="guias-buttons-container">
          {anexosTemp.length === 0 ? (
            <>
              <DefaultButton
                text="VISUALIZAR GUIA"
                onClick={handleClick}
                aria-label={`Visualizar guia ${props.guide.idSolic}, emitida em ${props.guide.emissao}, de status ${props.guide.status}`}
                style={{ minWidth: "210px" }}
              />
              {props.guide.statusAnexosSolic?.stsPermiteAnexo === "SIM" && (
                  <AnexoButton
                    adicionarAnexo={adicionarAnexoTemp}
                    removerAnexo={removerAnexoTemporario}
                    verAnexo={verAnexo}
                    anexos={[]}
                    listContainerId={listContainerId}
                  />
                )}
              {props.guide.cartasNegativa.length > 0 && (
                <button
                  style={{ ...accessibility.textStyleAccessibility }}
                  className="gerar-carta-button"
                  onClick={() => {
                    handleGetCartaNegativa();
                  }}
                >
                  Gerar Carta de Negativa
                </button>
              )}
            </>
          ) : (
            <div className="flex flex-col gap-2">
              <AnexoButton
                adicionarAnexo={adicionarAnexoTemp}
                removerAnexo={removerAnexoTemporario}
                verAnexo={() => {}}
                anexos={anexosTemp}
                listContainerId={listContainerId}
                hideUploadButton={false}
                allowMultipleUploads={true}
              />
              <div className="guias-buttons-container">
                <DefaultButton
                  text="CONFIRMAR ANEXO"
                  onClick={confirmarAnexo}
                  style={{ minWidth: "210px" }}
                />
                <DefaultButton
                  variant="outlined"
                  text="CANCELAR"
                  style={{ minWidth: "210px" }}
                  onClick={() => openModalAbandonarAnexo()}
                />
              </div>
            </div>
          )}
          <ModalAction
            showModal={!!modalAction}
            handleShowModal={modalAction?.handleShowModal}
            icon={modalAction?.icon}
            textOutlinedButton={modalAction?.textOutlinedButton || ""}
            textContainedButton={modalAction?.textContainedButton || ""}
            title={modalAction?.title || ""}
            subTitle={modalAction?.subTitle || ""}
            handleOutlinedButton={modalAction?.handleOutlinedButton}
            handleContainedButton={modalAction?.handleContainedButton}
            // colorContainedButton="success"
          />
        </div>
      </div>
    </div>
  );
}

export default MyGuidesCard;
